import { ANALYTICS_CATALOG, type AnalyticsEventName, assertAllowlisted, type CatalogProperties } from "./catalog";
import type { AnalyticsSink, AnalyticsValue } from "./sinks/types";

export const CONTEXT_KEYS = [
  "appVersion",
  "environment",
  "locale",
  "routePattern",
  "viewportClass",
  "isAdmin",
  "labCount",
  "labId",
  "testType",
  "testMethod",
  "testId",
  "processingTestId",
] as const;

export type ContextKey = (typeof CONTEXT_KEYS)[number];
export type AnalyticsContext = Partial<Record<ContextKey, AnalyticsValue>>;

const contextKeys = new Set<string>(CONTEXT_KEYS);
const context = new Map<ContextKey, AnalyticsValue>();
let activeSink: AnalyticsSink | null = null;

const BUFFER_CAP = 100;

interface BufferedEvent {
  event: AnalyticsEventName;
  properties: Record<string, AnalyticsValue>;
  context: Record<string, AnalyticsValue>;
}

const buffer: BufferedEvent[] = [];

function dispatch(send: () => void): void {
  try {
    send();
  } catch {
    return;
  }
}

function reconcileContext(sink: AnalyticsSink, snapshot: Record<string, AnalyticsValue>): void {
  for (const key of CONTEXT_KEYS) {
    if (!(key in snapshot)) {
      sink.unregister(key);
    }
  }
  if (Object.keys(snapshot).length > 0) {
    sink.register(snapshot);
  }
}

export function setActiveSink(sink: AnalyticsSink | null): void {
  activeSink = sink;
  if (!sink) {
    return;
  }
  for (const buffered of buffer.splice(0)) {
    dispatch(() => reconcileContext(sink, buffered.context));
    dispatch(() => sink.track(buffered.event, buffered.properties));
  }
  dispatch(() => reconcileContext(sink, Object.fromEntries(context)));
}

export function registerContext(values: AnalyticsContext): void {
  const stray = Object.keys(values).filter((key) => !contextKeys.has(key));
  if (stray.length > 0) {
    throw new Error(`analytics: ${stray.join(", ")} is not an allowlisted context key`);
  }
  const defined: Record<string, AnalyticsValue> = {};
  for (const key of CONTEXT_KEYS) {
    const value = values[key];
    if (value !== undefined) {
      context.set(key, value);
      defined[key] = value;
    }
  }
  if (Object.keys(defined).length > 0) {
    dispatch(() => activeSink?.register(defined));
  }
}

export function clearContext(keys: readonly ContextKey[]): void {
  for (const key of keys) {
    context.delete(key);
    dispatch(() => activeSink?.unregister(key));
  }
}

export function notify<E extends AnalyticsEventName>(event: E, properties?: CatalogProperties<E>): void {
  if (!(event in ANALYTICS_CATALOG)) {
    throw new Error(`analytics: event "${event}" is not registered in the catalog`);
  }
  assertAllowlisted(event, properties);
  const props = (properties ?? {}) as Record<string, AnalyticsValue>;
  const sink = activeSink;
  if (sink) {
    dispatch(() => sink.track(event, props));
    return;
  }
  buffer.push({ event, properties: props, context: Object.fromEntries(context) });
  if (buffer.length > BUFFER_CAP) {
    buffer.shift();
  }
}
